import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TermsConditionSectionProps } from "_interfaces/terms-conditions";
import { removeAllSlashes } from "functions/helper";
import RouteConstant from "navigation/constant";
import SectionTitle from "parts/sectionTitle";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export interface TermsSideNavProps {
  navListItem: TermsConditionSectionProps["ListItem"];
}

const TermsSideNav: React.FC<TermsSideNavProps> = ({ navListItem = [] }) => {
  const pathname = useLocation()?.pathname || "";
  const { breakpoints, palette } = useTheme();
  const isSmallDevice = useMediaQuery(breakpoints.down("md"));

  const stepRefs = useRef<(HTMLElement | null)[]>([]);

  const HEADER_HEIGHT = 100;

  const [activeStep, setActiveStep] = useState<number>(0);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  const stepperRef = useRef<HTMLDivElement | null>(null);

  const stickySx = {
    position: isSmallDevice ? "relative" : "sticky",
    top: isSmallDevice ? "unset" : `${HEADER_HEIGHT}px`,
    alignSelf: "flex-start",
    zIndex: 999,
    display: "block",
    width: "100%",
    maxWidth: isSmallDevice ? "100%" : "320px",
  };

  useEffect(() => {
    if (!navListItem || navListItem.length === 0) return;

    const currentPath = removeAllSlashes(pathname);

    const index = navListItem.findIndex((item) => {
      const itemPath = removeAllSlashes(
        pathname === RouteConstant.TERMS_CONDITIONS
          ? RouteConstant.TERMS_CONDITIONS
          : `${RouteConstant.TERMS_CONDITIONS}/${item.Link}`
      );
      return currentPath === itemPath;
    });

    if (index !== -1) setActiveStep(index);
  }, [pathname, navListItem]);

  // SMALL DEVICE - HORIZONTAL SCROLL
  useEffect(() => {
    if (!navListItem || navListItem.length === 0) return;

    const onScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      let newActive = 0;
      for (let i = 0; i < stepRefs.current.length; i++) {
        const el = stepRefs.current[i];
        if (!el) continue;
        if (el.offsetTop - HEADER_HEIGHT <= scrollPosition) {
          newActive = i;
        } else {
          break;
        }
      }

      if (newActive !== activeStep) setActiveStep(newActive);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [navListItem]);

  useEffect(() => {
    if (isSmallDevice && stepperRef.current) {
      const activeStepElement = stepperRef.current.querySelector(
        `.MuiStep-root:nth-child(${activeStep + 1})`
      ) as HTMLElement | null;

      if (activeStepElement) {
        const stepperWidth = stepperRef.current.offsetWidth;
        const stepWidth = activeStepElement.offsetWidth;
        const stepLeft = activeStepElement.offsetLeft;
        const scrollPosition = stepLeft - stepperWidth / 2 + stepWidth / 2;

        stepperRef.current.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  }, [activeStep, isSmallDevice]);

  return (
    <Box sx={stickySx} ref={stepperRef}>
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 2,
          px: 1,
          color: palette?.common.white,
          backgroundColor: palette?.common.black,
          textAlign: "center",
          borderTopLeftRadius: "14px",
          borderTopRightRadius: "14px",
        }}
      >
        <SectionTitle
          title="Terms & Conditions"
          sx={{
            fontSize: {
              xs: 18,
              sm: 26,
            },
            fontWeight: 600,
          }}
        />
      </Box>

      <Stepper
        ref={stepperRef}
        activeStep={activeStep}
        orientation={isSmallDevice ? "horizontal" : "vertical"}
        connector={null}
        sx={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          p: { xs: 0, md: 2 },
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          borderBottomLeftRadius: { xs: 0, md: "14px" },
          borderBottomRightRadius: { xs: 0, md: "14px" },
          border: "1px solid rgba(0, 0, 0, 0.05)",
          width: "100%",
          height: isSmallDevice ? "auto" : "420px",
          overflowY: "auto",
          display: "block",
          position: isSmallDevice ? "unset" : "sticky",
          top: isSmallDevice ? "unset" : HEADER_HEIGHT,
          overflowX: isSmallDevice ? "auto" : "visible",
          whiteSpace: isSmallDevice ? "nowrap" : "normal",
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: 3,
          },

          "&::-webkit-scrollbar-thumb": {
            background: "inherit",
            borderRadius: 3,
          },
        }}
      >
        {navListItem?.map((item, i) => {
          const isActive = activeStep === i;
          const isHovered = hoveredStep === i;

          const to =
            item?.Link === "terms-and-conditions"
              ? RouteConstant.TERMS_CONDITIONS
              : `${RouteConstant.TERMS_CONDITIONS}/${item.Link}`;

          return (
            <Step
              key={i}
              onMouseEnter={() => setHoveredStep(i)}
              onMouseLeave={() => setHoveredStep(null)}
              sx={{
                cursor: "pointer",
                display: isSmallDevice ? "inline-block" : "block",
                mb: isSmallDevice ? 0 : 0.5,
                position: "relative",
                transition: "all 0.3s ease",
                "&:last-child": {
                  mb: 0,
                },
                "&::before": !isSmallDevice
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "2px",
                      backgroundColor: isActive
                        ? palette.primary.main
                        : "transparent",
                      borderRadius: "0 4px 4px 0",
                      transition: "width 0.3s ease, background-color 0.3s ease",
                    }
                  : {},
                "&:hover::before": !isSmallDevice
                  ? {
                      width: "2px",
                      backgroundColor: isActive
                        ? palette.primary.main
                        : palette.primary.light,
                    }
                  : {},
              }}
            >
              <Box
                component="span"
                sx={{
                  py: 1.5,
                  px: isSmallDevice ? 2 : 2.5,
                  borderRadius: 2,
                  backgroundColor:
                    isActive || isHovered
                      ? "rgba(25, 118, 210, 0.04)"
                      : "transparent",
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                  transform: isHovered ? "translateX(4px)" : "translateX(0)",
                  "& .MuiStepLabel-iconContainer": { display: "none" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: isActive ? 600 : 500,
                  color: isActive
                    ? palette.primary.main + "!important"
                    : palette.text.secondary + "!important",
                  "&:hover": {
                    color: palette.primary.main,
                  },
                }}
              >
                {item.Label}

                <ArrowForwardIosIcon
                  sx={{
                    fontSize: 14,
                    color: isActive
                      ? palette.primary.main
                      : palette.action.disabled,
                    transform: isHovered ? "translateX(2px)" : "translateX(0)",
                    opacity: isSmallDevice
                      ? 0
                      : isActive || isHovered
                      ? 1
                      : 0.3,
                    transition: "all 0.3s ease",
                    ml: "auto",
                  }}
                />

                <Link className="stretched-link" to={to} />
              </Box>
            </Step>
          );
        })}
      </Stepper>

      {/* Progress indicator for mobile */}
      {/* {isSmallDevice && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: palette.text.secondary,
              fontWeight: 500,
            }}
          >
            {activeStep + 1} of {navListItem.length}
          </Typography>
          <Box
            sx={{
              height: 4,
              flex: 1,
              maxWidth: 200,
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${((activeStep + 1) / navListItem.length) * 100}%`,
                backgroundColor: palette.primary.main,
                borderRadius: 2,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      )} */}
    </Box>
  );
};

export default TermsSideNav;
